<template>
  <div class="step-panel" data-testid="payroll-step2-rules-preview">
    <!-- Step Header -->
    <div class="step-header">
      <h2>{{ t('Regras de Pagamento') }}</h2>
    </div>

    <!-- Helper Banner -->
    <div class="helper-banner">
      <i class="pi pi-info-circle"></i>
      <div class="helper-content">
        <strong>{{ t('Regras de Cálculo') }}</strong>
        <ul>
          <li>{{ t('As regras de pagamento determinam como as comissões e salários serão calculados') }}</li>
          <li>{{ t('Diferentes tipos de regras podem ser executados: comissão sobre executado, vendido remotamente, ou valor fixo') }}</li>
          <li>{{ t('Revise as regras cuidadosamente antes de prosseguir para os cálculos') }}</li>
        </ul>
      </div>
    </div>

    <!-- Warning Banner -->
    <div class="warning-banner">
      <i class="pi pi-exclamation-triangle"></i>
      <div class="warning-content">
        <strong>{{ t('Atenção') }}</strong>
        <p>{{ t('Estas regras serão usadas para todos os cálculos subsequentes. Certifique-se de que estão corretas antes de prosseguir.') }}</p>
      </div>
    </div>

    <!-- Rule Summary Card -->
    <div class="rule-summary">
      <div class="summary-header">
        <i class="pi pi-file-text"></i>
        <h3>{{ t('Resumo das Regras') }}</h3>
      </div>
      <div class="summary-content">
        <div class="summary-row">
          <span class="summary-label">{{ t('Total de Regras:') }}</span>
          <span class="summary-value">{{ enrichedRules.length }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">{{ t('Salário Fixo:') }}</span>
          <span class="summary-value">{{ getRuleCountByType('fixed') }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">{{ t('Comissão Executada:') }}</span>
          <span class="summary-value">{{ getRuleCountByType('commission_over_gross_executed') }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">{{ t('Comissão Remota:') }}</span>
          <span class="summary-value">{{ getRuleCountByType('commission_over_gross_sold_remotely') }}</span>
        </div>
      </div>
    </div>

    <!-- Important Notes -->
    <div class="important-notes">
      <div class="notes-header">
        <i class="pi pi-info-circle"></i>
        <h3>{{ t('Próximos Passos') }}</h3>
      </div>
      <ul class="notes-list">
        <li>
          <i class="pi pi-check-circle"></i>
          <span>{{ t('Se houver regras de receita bruta: prossiga para a conciliação de receitas') }}</span>
        </li>
        <li>
          <i class="pi pi-check-circle"></i>
          <span>{{ t('Se houver regras de comissão executada: prossiga para os adiantamentos') }}</span>
        </li>
        <li>
          <i class="pi pi-check-circle"></i>
          <span>{{ t('Se houver reembolsos pendentes: prossiga para a aprovação de reembolsos') }}</span>
        </li>
        <li>
          <i class="pi pi-check-circle"></i>
          <span>{{ t('Em seguida, adicione deduções se necessário') }}</span>
        </li>
      </ul>
    </div>

    <p class="step-description">
      {{ t('As seguintes regras serão executadas para este período') }}:
    </p>

    <div class="rules-preview-container">
      <div v-if="enrichedRules.length === 0" class="no-rules-message">
        <div class="empty-state-icon">
          <i class="pi pi-inbox"></i>
        </div>
        <h4>{{ t('Sem regras para exibir') }}</h4>
        <p>{{ t('Nenhuma regra de pagamento foi encontrada para este período') }}</p>
      </div>

      <div v-for="rule in enrichedRules" :key="rule.id" class="rule-item">
        <div class="rule-title">
          <span class="rule-number">#{{ rule.id }}</span>
          <span class="rule-badge">{{ getRuleTypeLabel(rule.rule_type) }}</span>
        </div>

        <div class="rule-details">
          <div class="detail-row">
            <strong>{{ t('Franquia') }}:</strong>
            <span>{{ rule.franchise_name }}</span>
          </div>

          <div class="detail-row">
            <strong>{{ t('Colaborador') }}:</strong>
            <span>{{ rule.user_name }}</span>
          </div>

          <div class="detail-row amount-row">
            <strong>{{ t('Valor') }}:</strong>
            <span class="amount">
              {{ rule.rule_type?.includes('commission')
                ? `${(parseFloat(String(rule.amount ?? 0)) * 100).toFixed(2)}%`
                : n(parseFloat(String(rule.amount ?? 0)) || 0, 'currency')
              }}
            </span>
          </div>

          <div class="detail-row">
            <strong>{{ t('Frequência') }}:</strong>
            <span>{{ getFrequencyLabel(rule.frequency) }}</span>
          </div>
        </div>
      </div>
    </div>

    <inline-message severity="info" :closable="false" class="summary-message">
      <i class="pi pi-check-circle"></i>
      {{ t('Total de regras para executar') }}: <strong>{{ enrichedRules.length }}</strong>
    </inline-message>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { permissionsService } from '@/services/permissionsService';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface PaymentRule {
  id?: Id;
  rule_type?: string;
  franchise_id?: Id;
  franchise_name?: string;
  user_id?: Id;
  user_name?: string;
  amount?: number | string;
  frequency?: string;
  [key: string]: unknown;
}

interface EnrichedRule extends PaymentRule {
  franchise_name: string;
  user_name: string;
}

interface WizardMachine {
  context: { value: {
    rules?: PaymentRule[];
  } };
  currentState?: { value: string };
}

// =============================================================================
// INJECT MACHINE - Single Source of Truth
// =============================================================================

const machine = inject<WizardMachine>('wizardMachine')!;

const { t, n } = useI18n();

// =============================================================================
// REACTIVE DATA FROM MACHINE
// =============================================================================

// Get rules from machine context
const rules = computed<PaymentRule[]>(() => {
  const machineRules = machine.context.value.rules || [];
  log('Step2RulesPreview', 'rules', 'Getting rules from machine context', {
    rulesCount: machineRules.length,
    machineState: machine.currentState?.value,
    contextKeys: Object.keys(machine.context.value || {})
  });
  return machineRules;
});

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get rule type label
 */
function getRuleTypeLabel(ruleType: string | undefined): string {
  const labels: Record<string, string> = {
    'commission_over_gross_executed': t('Comissão sobre executado'),
    'commission_over_gross_sold_remotely': t('Comissão sobre vendido remotamente'),
    'fixed': t('Valor fixo')
  };
  return labels[ruleType ?? ''] || (ruleType ?? '');
}

/**
 * Get frequency label
 */
function getFrequencyLabel(frequency: string | undefined): string {
  const labels: Record<string, string> = {
    'weekly': t('Semanal'),
    'biweekly': t('Quinzenal'),
    'monthly': t('Mensal')
  };
  return labels[frequency ?? ''] || (frequency ?? '');
}

/**
 * Get franchise name from franchise_id using permissionsService
 */
const getFranchiseName = (franchiseId: Id | undefined): string => {
  if (!franchiseId) return '';

  try {
    // permissionsService.getFranquias() return shape varies in JS land; cast.
    const franchises = permissionsService.getFranquias() as Record<string, { Dados?: { Nome?: string } }> | null | undefined;
    const franchiseIdStr = String(franchiseId);

    if (franchises && franchises[franchiseIdStr]) {
      return franchises[franchiseIdStr].Dados?.Nome || '';
    }

    return '';
  } catch (err) {
    log('Step2RulesPreview', 'getFranchiseName', 'ERROR getting franchise name', { franchiseId, error: (err as Error)?.message });
    return '';
  }
};

/**
 * Get employee name from user_id using permissionsService
 */
const getEmployeeName = (userId: Id | undefined, franchiseId: Id | undefined): string => {
  if (!userId || !franchiseId) return '';

  try {
    const employees = permissionsService.getFuncionariosByFranquiaID(Number(franchiseId)) as Record<string, { funcionario_nome?: string }> | null | undefined;

    if (employees && employees[String(userId)]) {
      return employees[String(userId)].funcionario_nome || '';
    }

    return '';
  } catch (err) {
    log('Step2RulesPreview', 'getEmployeeName', 'ERROR getting employee name', { userId, franchiseId, error: (err as Error)?.message });
    return '';
  }
};

// Enrich rules with franchise and employee names
const enrichedRules = computed<EnrichedRule[]>(() => {
  return rules.value.map((rule) => ({
    ...rule,
    franchise_name: getFranchiseName(rule.franchise_id) || rule.franchise_name || 'N/A',
    user_name: getEmployeeName(rule.user_id, rule.franchise_id) || rule.user_name || 'N/A'
  }));
});

/**
 * Get rule count by type
 */
function getRuleCountByType(ruleType: string): number {
  return enrichedRules.value.filter((r) => r.rule_type === ruleType).length;
}
</script>

<style scoped>
/* Step Header */
.step-header h2 {
  margin: 0 0 1rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
}

/* Helper Banner */
.helper-banner {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: var(--blue-50);
  border-left: 4px solid var(--blue-500);
  border-radius: 6px;
  margin-bottom: 1rem;
}

.helper-banner i {
  color: var(--blue-500);
  font-size: 1.5rem;
  flex-shrink: 0;
}

.helper-content strong {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--blue-700);
}

.helper-content ul {
  margin: 0;
  padding-left: 1.25rem;
}

.helper-content li {
  margin-bottom: 0.25rem;
  color: var(--blue-900);
  font-size: 0.9rem;
}

/* Warning Banner */
.warning-banner {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: var(--orange-50);
  border-left: 4px solid var(--orange-500);
  border-radius: 6px;
  margin-bottom: 1rem;
}

.warning-banner i {
  color: var(--orange-500);
  font-size: 1.5rem;
  flex-shrink: 0;
}

.warning-content strong {
  display: block;
  margin-bottom: 0.25rem;
  color: var(--orange-700);
}

.warning-content p {
  margin: 0;
  color: var(--orange-900);
  font-size: 0.9rem;
  line-height: 1.5;
}

/* Rule Summary & Important Notes */
.rule-summary,
.important-notes {
  padding: 1.25rem;
  background: var(--surface-0);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  margin-bottom: 1rem;
}

.summary-header,
.notes-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--surface-border);
}

.summary-header i,
.notes-header i {
  font-size: 1.25rem;
  color: var(--primary-color);
}

.summary-header h3,
.notes-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.summary-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.95rem;
}

.summary-label {
  color: var(--text-color-secondary);
  font-weight: 500;
}

.summary-value {
  color: var(--text-color);
  font-weight: 600;
  text-align: right;
}

.notes-list {
  margin: 0;
  padding-left: 0;
  list-style: none;
}

.notes-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
  color: var(--text-color);
  line-height: 1.5;
}

.notes-list li i {
  color: var(--primary-color);
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.step-panel {
  width: 100%;
}

.step-description {
  margin-bottom: 1.5rem;
  color: var(--text-color-secondary);
  font-size: 0.95rem;
  line-height: 1.5;
}

/* Rules preview container */
.rules-preview-container {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  max-height: 550px;
  overflow-y: auto;
  padding: 0.5rem;
  padding-right: 0.75rem;
}

/* Scrollbar styling */
.rules-preview-container::-webkit-scrollbar {
  width: 8px;
}

.rules-preview-container::-webkit-scrollbar-track {
  background: var(--surface-100);
  border-radius: 4px;
}

.rules-preview-container::-webkit-scrollbar-thumb {
  background: var(--surface-400);
  border-radius: 4px;
}

.rules-preview-container::-webkit-scrollbar-thumb:hover {
  background: var(--surface-500);
}

/* Empty state */
.no-rules-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 3rem 2rem;
  background: var(--surface-50);
  border: 2px dashed var(--surface-300);
  border-radius: 12px;
  color: var(--text-color-secondary);
  text-align: center;
}

.empty-state-icon {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface-0);
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.empty-state-icon i {
  font-size: 2rem;
  color: var(--surface-400);
}

.no-rules-message h4 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-color);
}

.no-rules-message p {
  margin: 0;
  font-size: 0.875rem;
  max-width: 320px;
}

/* Rule item */
.rule-item {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.5rem;
}

.rule-item:hover {
  border-color: var(--primary-300);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.rule-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
}

.rule-number {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.rule-badge {
  background: var(--primary-100);
  color: var(--primary-700);
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.rule-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  border-radius: 4px;
  background: #fafafa;
}

.detail-row strong {
  color: var(--text-color-secondary);
  font-size: 0.875rem;
  font-weight: 600;
}

.detail-row span {
  color: var(--text-color);
  font-size: 0.9375rem;
}

.amount-row {
  background: var(--primary-50);
  border-left: 3px solid var(--primary-500);
}

.amount-row .amount {
  font-weight: 700;
  font-size: 1.125rem;
  color: var(--primary-600);
}

/* Summary message */
.summary-message {
  margin-top: 1.5rem;
  border-radius: 8px;
}

.summary-message strong {
  font-weight: 700;
  color: var(--primary-700);
}

.summary-message i {
  margin-right: 0.5rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .detail-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .rule-title {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .rules-preview-container {
    max-height: 450px;
  }
}
</style>
